import { AfterViewInit, Component, ElementRef, ViewChild, inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ICellRendererParams } from 'ag-grid-community';
import { CommonModule } from '@angular/common';
import {
  CustomerDialogData,
  EditCustomerListComponent,
} from '../../../Dialogs/edit-customer-list/edit-customer-list.component';
import { RbacService } from '../../../services/rbac/rbac.service';
import { RbacPermissions } from '../../../services/rbac/rbac-permissions';

@Component({
  selector: 'app-button-cell-renderer-customer-list',
  standalone: true,
  imports: [MatIconModule, CommonModule],
  template: `
    <span class="action-buttons" *ngIf="params && (canEdit || canDelete)">
      <button
        #actionButton
        type="button"
        class="action-btn action-btn--edit"
        title="Edit"
        *ngIf="canEdit"
        (click)="onEdit($event)"
      >
        <mat-icon>edit</mat-icon>
      </button>
      <button
        type="button"
        class="action-btn action-btn--delete"
        title="Delete"
        *ngIf="canDelete"
        (click)="onDelete($event)"
      >
        <mat-icon>delete</mat-icon>
      </button>
    </span>
  `,
  styles: [
    `
      .action-buttons {
        display: flex;
        gap: 6px;
        align-items: center;
        height: 100%;
      }

      .action-btn {
        border: none;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border-radius: 6px;
        cursor: pointer;
      }

      .action-btn mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        line-height: 18px;
      }

      .action-btn--edit {
        background: #e8f0f2;
        border: 1px solid #c5d8dc;
      }
      .action-btn--edit mat-icon {
        color: #3a6570;
      }

      .action-btn--delete {
        background: #fdecea;
        border: 1px solid #f5b8b2;
      }
      .action-btn--delete mat-icon {
        color: #d32f2f;
      }
    `,
  ],
})
export class ButtonCellRendererCustomerListComponent
  implements ICellRendererAngularComp, AfterViewInit
{
  private readonly rbacService = inject(RbacService);
  private readonly dialog = inject(MatDialog);

  params!: ICellRendererParams & {
    onCustomerSaved?: () => void;
    onDelete?: (row: Record<string, unknown>) => void;
  };

  canEdit = false;
  canDelete = false;

  @ViewChild('actionButton') actionButton!: ElementRef<HTMLButtonElement>;

  ngAfterViewInit(): void {
    this.actionButton?.nativeElement?.focus();
  }

  agInit(params: ICellRendererParams): void {
    this.params = params as typeof this.params;
    this.refreshPermissions();
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params as typeof this.params;
    this.refreshPermissions();
    return true;
  }

  private refreshPermissions(): void {
    this.canEdit = this.rbacService.hasPermission(
      RbacPermissions.UserMgmt_Customer_Edit
    );
    this.canDelete = this.rbacService.hasPermission(
      RbacPermissions.UserMgmt_Customer_Delete
    );
  }

  onEdit(event: Event): void {
    event.stopPropagation();
    const dialogData: CustomerDialogData = {
      mode: 'edit',
      rowData: this.params.node?.data || {},
    };
    const dialogRef = this.dialog.open(EditCustomerListComponent, {
      width: '80vw',
      maxWidth: '1000px',
      panelClass: 'customer-dialog-panel',
      data: dialogData,
      restoreFocus: false,
      closeOnNavigation: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      document.body.removeAttribute('inert');
      if (result?.success) {
        this.params?.onCustomerSaved?.();
      }
    });
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    const data = this.params?.data;
    if (data) {
      this.params?.onDelete?.(data as Record<string, unknown>);
    }
  }
}
